import { useMedicalServiceMedicalDirectionsList } from '@insurance/api/queries'
import { MedicalService } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber } from '@shared/components'
import { usePermissionRestriction } from '@shared/hooks'
import { AppPermissions } from '@shared/types'
import { useQuery } from '@tanstack/react-query'
import { Col, Form, Row, Select } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHealthInsuranceValues } from './HealthInsuranceContext'
import { MAX_INSURED_AMOUNT } from './utils'

export const InsuranceAmount = () => {
  const { t } = useTranslation()
  const [disabled, setDisabled] = useState(false)
  const formInstance = Form.useFormInstance()
  const { isEditable } = useHealthInsuranceValues()
  const { minAmount: minAmountValidation } = useValidationsField()
  const { hasPermission } = usePermissionRestriction()

  const directionsWatch = Form.useWatch('directions')
  const insurance = Form.useWatch('insurance')

  const { data: directionsData } = useMedicalServiceMedicalDirectionsList({
    search: ''
  })

  const firstDirectionId = directionsData?.results[0]?.id

  const directions = directionsWatch?.length
    ? directionsWatch
    : firstDirectionId
    ? [firstDirectionId]
    : []

  const { data, isLoading } = useQuery({
    queryFn: () =>
      MedicalService.medicalTravelInsurancesDirectionsPricesCreate({
        directions
      }),
    queryKey: [
      MedicalService.medicalTravelInsurancesDirectionsPricesCreate.name,
      directions?.join('')
    ],
    enabled: !!directions?.length
  })

  useEffect(() => {
    if (data?.length === 1) setDisabled(true)
    else setDisabled(false)

    formInstance.setFieldValue('insurance', data?.[0]?.id)
  }, [data, formInstance])

  const minAmount = data?.find(({ id }) => id === insurance)?.amount || 0

  return (
    <Row gutter={[12, 12]}>
      <Col xs={{ span: 24 }} md={{ span: 12 }}>
        <Form.Item label={t('insured_amount')} name="insurance">
          <Select
            placeholder={t('insured_amount')}
            options={data?.map(({ id, amount }) => ({
              label: amount,
              value: id
            }))}
            loading={isLoading}
            disabled={disabled || !directions?.length || isEditable}
          />
        </Form.Item>
      </Col>

      <Col xs={{ span: 24 }} md={{ span: 12 }}>
        <Form.Item label={t('currency')} name="insurance">
          <Select
            disabled
            placeholder={t('currency')}
            options={data?.map(({ id, currency }) => ({
              label: currency,
              value: id
            }))}
            loading={isLoading}
          />
        </Form.Item>
      </Col>

      {hasPermission([AppPermissions.APPROVE_ORDER]) && (
        <Col span={24}>
          <Form.Item
            label={t('custom_amount')}
            name="custom_amount"
            rules={[minAmountValidation(minAmount)]}
          >
            <InputParsedNumber
              min={minAmount + 1}
              max={MAX_INSURED_AMOUNT}
              placeholder={t('custom_amount')}
              disabled={isEditable}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  )
}
